import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Divider,
  TextField,
} from '@mui/material';
import {
  Save,
  ArrowBack,
  Preview,
} from '@mui/icons-material';
import { RootState } from '../store';
import { fetchItemById, saveItem, setCurrentItem, clearCurrentItem } from '../store/slices/itemsSlice';
import { AuthorItem } from '../types/item';
import QuestionTextEditor from '../components/ItemEditor/QuestionTextEditor';
import ExplanationEditor from '../components/ItemEditor/ExplanationEditor';
import AnswerOptionsSection from '../components/ItemEditor/AnswerOptionsSection';
import TagFields from '../components/ItemEditor/TagFields';
import PreviewPanel from '../components/ItemEditor/PreviewPanel';

const ItemEditorPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentItem, isEditing, isSaving, saveError } = useSelector((state: RootState) => state.items);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

  const isNewItem = !id || id === 'new';

  useEffect(() => {
    if (isNewItem) {
      // Create new item template aligned with learning-service
      const newItem: Partial<AuthorItem> = {
        question: '',
        options: ['', '', '', ''],
        answerIndex: 0,
        explanation: '',
        tags: [],
        hints: [],
        score: 1,
        duration: 60,
      };
      dispatch(setCurrentItem(newItem as AuthorItem));
    } else {
      dispatch(fetchItemById(id) as any);
    }

    return () => {
      dispatch(clearCurrentItem());
    };
  }, [dispatch, id, isNewItem]);

  const handleSave = async () => {
    if (!currentItem) return;
    
    try {
      await dispatch(saveItem(currentItem) as any).unwrap();
      setHasUnsavedChanges(false);
      if (isNewItem) {
        navigate('/items');
      }
    } catch (error) {
      console.error('Failed to save item:', error);
    }
  };

  const handleBack = () => {
    if (hasUnsavedChanges) {
      if (window.confirm('You have unsaved changes. Are you sure you want to leave?')) {
        navigate('/items');
      }
    } else {
      navigate('/items');
    }
  };

  const handleItemChange = (updates: Partial<AuthorItem>) => {
    if (currentItem) {
      dispatch(setCurrentItem({ ...currentItem, ...updates } as AuthorItem));
      setHasUnsavedChanges(true);
    }
  };

  if (!currentItem) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ display: 'flex', height: '100%' }}>
      {/* Main Content Area */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        {/* Header */}
        <Paper sx={{ p: 2, borderRadius: 0, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={handleBack}>
                <ArrowBack />
              </IconButton>
              <Typography variant="h5" sx={{ fontWeight: 600 }}>
                {isNewItem ? '📝 Create New Item' : '✏️ Edit Item'}
              </Typography>
              {hasUnsavedChanges && (
                <Typography variant="body2" color="warning.main">
                  • Unsaved changes
                </Typography>
              )}
            </Box>
            
            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="outlined"
                startIcon={<Preview />}
                onClick={() => {/* TODO: Open preview modal */}}
              >
                Preview
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={isSaving}
              >
                {isSaving ? 'Saving...' : 'Save'}
              </Button>
            </Box>
          </Box>
          
          {saveError && (
            <Typography variant="body2" color="error" sx={{ mt: 1 }}>
              Error: {saveError}
            </Typography>
          )}
        </Paper>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: 'auto', p: 3 }}>
          {/* Question Text Editor */}
          <QuestionTextEditor
            value={currentItem.question}
            onChange={(question) => handleItemChange({ question })}
          />

          {/* Media URL Section */}
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              🎵 Media URL
            </Typography>
            <TextField
              fullWidth
              label="Media URL (for listening questions)"
              value={currentItem.mediaUrl || ''}
              onChange={(e) => handleItemChange({ mediaUrl: e.target.value })}
              placeholder="https://example.com/audio.mp3"
              helperText="Enter URL for audio or video content"
            />
          </Paper>

          {/* Explanation Editor */}
          <ExplanationEditor
            value={currentItem.explanation || ''}
            onChange={(explanation) => handleItemChange({ explanation })}
          />

          {/* Answer Options */}
          <AnswerOptionsSection
            options={currentItem.options}
            correctAnswerIndex={currentItem.answerIndex || 0}
            onOptionsChange={(options) => handleItemChange({ options })}
            onCorrectAnswerChange={(answerIndex) => handleItemChange({ answerIndex })}
          />
        </Box>
      </Box>

      {/* Right Sidebar */}
      <Box sx={{ 
        width: 320, 
        borderLeft: 1, 
        borderColor: 'divider',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'auto'
      }}>
        {/* Basic Metadata Panel */}
        <Box sx={{ p: 2 }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            📋 Metadata
          </Typography>

          {/* Tags */}
          <TagFields
            tags={currentItem.tags}
            onChange={(tags) => handleItemChange({ tags })}
          />

          {/* Score */}
          <TextField
            label="Score"
            type="number"
            fullWidth
            size="small"
            value={currentItem.score || 1}
            onChange={(e) => handleItemChange({ score: Number(e.target.value) })}
            sx={{ mb: 2 }}
            inputProps={{ min: 1, max: 10 }}
          />

          {/* Duration */}
          <TextField
            label="Duration (seconds)"
            type="number"
            fullWidth
            size="small"
            value={currentItem.duration || 60}
            onChange={(e) => handleItemChange({ duration: Number(e.target.value) })}
            sx={{ mb: 2 }}
            inputProps={{ min: 30, max: 600 }}
          />
        </Box>

        <Divider />

        {/* Preview Panel */}
        <PreviewPanel item={currentItem} />
      </Box>
    </Box>
  );
};

export default ItemEditorPage;
